import { ChevronDownIcon, XCircleFillIcon } from "@primer/octicons-react";
import { Box, Button, SelectPanel } from "@primer/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { foDotDangKyAnApi } from "../../api/foDotDangKyAnApi";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { IFoDotDangKyAn } from "../../models/response/thuc-don/IFoDotDangKyAn";

interface IComboboxDotDangKyAnProps {
  isReadonly?: boolean;
  value?: number;
  onValueChanged: (id: number, data?: any) => void;
  className?: string;
  preText?: string;
  width?: string | number;
  stylingMode?: "outlined" | "filled" | "underlined";
  isShowClearButton?: boolean;
  isShowAll?: boolean;
  nam_hoc?: string;
  isAutoSelectLast?: boolean;
}

const ComboboxDotDangKyAn = (props: IComboboxDotDangKyAnProps) => {
  const { dm_coso_id, nam_hoc: contextNamHoc } = useCommonSelectedHook();
  const currentNamHoc = props.nam_hoc || contextNamHoc;
  const [dotDangKyAn, setDotDangKyAn] = useState<IFoDotDangKyAn[]>([]);

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const valueRef = useRef(props.value);
  const onValueChangedRef = useRef(props.onValueChanged);
  valueRef.current = props.value;
  onValueChangedRef.current = props.onValueChanged;

  const { translate } = useCommonContext();

  const selectableDots = useMemo(() => {
    if (props.isShowAll) {
      return dotDangKyAn;
    }
    return dotDangKyAn.filter(
      (x) => x.is_active && x.fo_dotdangky_an_status_id === 1
    );
  }, [dotDangKyAn, props.isShowAll]);

  const dataSource = useMemo(() => {
    return selectableDots.map((x) => ({ id: x.id, text: x.dot_dang_ky }));
  }, [selectableDots]);

  const filteredItems = useMemo(() => {
    return dataSource.filter((item) =>
      item.text.toLowerCase().includes(filter.toLowerCase())
    );
  }, [dataSource, filter]);

  const selected = useMemo(() => {
    return dataSource.find((x) => x.id === props.value);
  }, [dataSource, props.value]);

  useEffect(() => {
    let cancelled = false;

    const fetchDotDangKy = async () => {
      if (!dm_coso_id || !currentNamHoc) {
        setDotDangKyAn([]);
        return;
      }

      try {
        const res = await foDotDangKyAnApi.select_by_coso_namhoc(
          dm_coso_id,
          currentNamHoc
        );
        if (cancelled) return;

        if (res?.is_success && Array.isArray(res.data)) {
          const list: IFoDotDangKyAn[] = res.data.filter((x: any) => x.is_active);
          setDotDangKyAn(list);

          if (props.isAutoSelectLast && list.length > 0) {
            const selectable = props.isShowAll
              ? list
              : list.filter((x) => x.fo_dotdangky_an_status_id === 1);

            if (selectable.length > 0) {
              const currentVal = valueRef.current ?? 0;
              const isStillValid =
                currentVal > 0 && selectable.some((x) => x.id === currentVal);

              if (!isStillValid) {
                const lastItem = selectable[selectable.length - 1];
                onValueChangedRef.current(lastItem.id, lastItem);
              }
            }
          }
        } else {
          setDotDangKyAn([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Lỗi lấy danh sách đợt đăng ký ăn:", err);
          setDotDangKyAn([]);
        }
      }
    };

    fetchDotDangKy();

    return () => {
      cancelled = true;
    };
  }, [dm_coso_id, currentNamHoc, props.isAutoSelectLast, props.isShowAll]);

  const setSelected = (selecteds: any) => {
    if (selecteds) props.onValueChanged(selecteds.id);
  };

  return (
    <SelectPanel
      renderAnchor={({
        children,
        "aria-labelledby": ariaLabelledBy,
        ...anchorProps
      }) => (
        <Button
          trailingAction={ChevronDownIcon}
          aria-labelledby={` ${ariaLabelledBy}`}
          {...anchorProps}
          disabled={props.isReadonly}
          sx={{
            width: "100%",
          }}
        >
          <p
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {children || translate("Chọn đợt đăng ký")}
          </p>
        </Button>
      )}
      title={
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>{translate("Chọn đợt đăng ký")}</Box>
            {props.isShowClearButton && (props.value ?? 0) > 0 && (
              <Button
                trailingVisual={XCircleFillIcon}
                variant="invisible"
                sx={{
                  color: "danger.emphasis",
                }}
                onClick={() => {
                  props.onValueChanged(0);
                }}
              >
                Bỏ chọn
              </Button>
            )}
          </Box>
        </>
      }
      placeholderText="Search"
      open={open}
      onOpenChange={setOpen}
      items={filteredItems}
      selected={selected}
      onSelectedChange={setSelected}
      onFilterChange={setFilter}
      showItemDividers={true}
      overlayProps={{ width: "small", height: "medium" }}
    />
  );
};

export default ComboboxDotDangKyAn;
